import React from "react";
import { formatMonthLong } from "../utils/months";

interface SelectedMonthCardProps {
  selectedMonth: string; // YYYY-MM
  rolloverIn: number; // unused allowance carried in from last month
  available: number; // monthly allowance + rolloverIn — what's actually spendable
  consumed: number;
  remaining: number;
}

/** Headline status card. Header reads "May 2026 Credits" or similar to match
 * the selected month. Consumption is measured against the available pool
 * (monthly allowance + any credits rolled over from last month). Overage state
 * turns the headline number and the "Over by X" footnote red. The "Learn more
 * about credits" link sits as a discreet footer. */
export function SelectedMonthCard({
  selectedMonth,
  rolloverIn,
  available,
  consumed,
  remaining,
}: SelectedMonthCardProps) {
  const isOverage = remaining < 0;
  const percentageUsed =
    available > 0 ? Math.min(100, (consumed / available) * 100) : 0;
  const fmt = (n: number) =>
    n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const hasRollover = rolloverIn > 0;
  const baseAllowance = available - rolloverIn; // this month's fresh grant

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm h-full">
      <div className="p-5 flex flex-col">
        <h2 className="text-lg font-semibold text-muted-foreground text-sm uppercase">
          {formatMonthLong(selectedMonth)} Credits
        </h2>
        <div className="mt-2 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-end mb-2">
              <span
                className={
                  isOverage
                    ? "font-mono text-3xl font-bold text-destructive"
                    : "font-mono text-3xl font-bold text-primary"
                }
              >
                {fmt(consumed)}
              </span>
              <div className="text-right">
                <span className="block text-sm text-muted-foreground">
                  of {fmt(available)} credits
                </span>
                {hasRollover && (
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {fmt(baseAllowance)} allowance + {fmt(rolloverIn)} rolled over
                  </span>
                )}
              </div>
            </div>
            <progress
              className={
                isOverage
                  ? "w-full h-3 rounded-full bg-muted accent-destructive [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-destructive [&::-moz-progress-bar]:bg-destructive"
                  : "w-full h-3 rounded-full bg-muted accent-primary [&::-webkit-progress-bar]:rounded-full [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary"
              }
              value={Math.min(consumed, available)}
              max={available > 0 ? available : 1}
            ></progress>
            <div className="flex justify-between mt-2 text-xs">
              <span
                className={
                  isOverage ? "text-destructive font-semibold" : "text-muted-foreground"
                }
              >
                {isOverage
                  ? `Over by ${fmt(-remaining)}`
                  : `${percentageUsed.toFixed(1)}% used`}
              </span>
            </div>
          </div>
          <div className="mt-3 text-xs text-right">
            <a
              href="https://docs.composo.ai/documentation/billing/credits"
              target="_blank"
              rel="noopener noreferrer"
              className="!text-muted-foreground !no-underline hover:!text-primary"
            >
              Learn more about credits ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
